/**
 * Botanical Watering & Gradual Withering Engine
 *
 * Implements realistic gradual withering when flowers are not watered for 3 days (72 hours).
 * Also provides hydration calculation, stage labeling, and visual distortion parameters.
 */

export type HydrationStage = 'thriving' | 'thirsty' | 'wilting' | 'withered';

export interface HydrationState {
  hydrationPercent: number; // 0 to 100
  witherLevel: number; // 0 (fresh) to 100 (fully withered)
  hoursElapsed: number;
  daysElapsed: number;
  stage: HydrationStage;
  stageLabel: string;
  stageBadgeColor: string;
  timeSinceText: string;
  needsWater: boolean;
  stemDroopDeg: number;
  headDroopDeg: number;
  leafDroopDeg: number;
  saturation: number;
  sepia: number;
  brightness: number;
}

/**
 * Calculates the current hydration and withering state based on last watering timestamp
 * Default withering period is 3 days (72 hours).
 */
export function calculateHydrationState(
  lastWateredAt?: string,
  intervalDays: number = 3
): HydrationState {
  const maxHours = Math.max(1, intervalDays) * 24; // 72 hours for 3 days
  const now = Date.now();
  let msElapsed = 0;

  if (lastWateredAt) {
    const parsed = Date.parse(lastWateredAt);
    if (!isNaN(parsed)) {
      msElapsed = Math.max(0, now - parsed);
    }
  }

  const hoursElapsed = msElapsed / (1000 * 60 * 60);
  const daysElapsed = hoursElapsed / 24;

  // Normalized progress from 0 (just watered) to 1 (passed 3 days)
  const witherProgress = Math.min(1, Math.max(0, hoursElapsed / maxHours));

  // Botanical non-linear withering curve:
  // First 18 hours: plant maintains turgidity (cellular osmotic pressure)
  // Day 2 (18-48h): gradual softening, slight drooping
  // Day 3 (48-72h): accelerated plasmolyzation, deep wilting
  // Past 72h: complete withered dormancy
  let turgor: number;
  if (witherProgress <= 0.25) {
    // 0 - 18h: 100% down to 88%
    turgor = 1 - (witherProgress / 0.25) * 0.12;
  } else if (witherProgress <= 0.66) {
    // 18h - 48h (Day 1-2): 88% down to 40%
    const subP = (witherProgress - 0.25) / (0.66 - 0.25);
    turgor = 0.88 - subP * 0.48;
  } else {
    // 48h - 72h (Day 2-3): 40% down to 0%
    const subP = (witherProgress - 0.66) / (1 - 0.66);
    turgor = Math.max(0, 0.40 - subP * 0.40);
  }

  const hydrationPercent = Math.round(turgor * 100);
  const witherLevel = 100 - hydrationPercent;

  let stage: HydrationStage;
  let stageLabel: string;
  let stageBadgeColor: string;

  if (hydrationPercent >= 75) {
    stage = 'thriving';
    stageLabel = 'Radiantes y Vivas';
    stageBadgeColor = 'emerald';
  } else if (hydrationPercent >= 40) {
    stage = 'thirsty';
    stageLabel = 'Sedientas (Pidiendo agua)';
    stageBadgeColor = 'amber';
  } else if (hydrationPercent > 5) {
    stage = 'wilting';
    stageLabel = 'Marchitándose gradualmente';
    stageBadgeColor = 'orange';
  } else {
    stage = 'withered';
    stageLabel = 'Marchitas por falta de agua (3+ días)';
    stageBadgeColor = 'rose';
  }

  // Visual offsets for realistic botanical physics
  // As witherLevel increases (0 -> 100):
  const droopFactor = witherLevel / 100;
  const stemDroopDeg = droopFactor * 22; // stems bow sideways
  const headDroopDeg = droopFactor * 48; // flower head nods downward
  const leafDroopDeg = droopFactor * 32; // leaves curl and droop toward soil

  // Botanical color degradation:
  // Fresh: saturation 1, sepia 0, brightness 1
  // Withered: saturation drops to 0.40, sepia rises to 0.42 (dried petal look), brightness down to 0.82
  const saturation = 1 - droopFactor * 0.58;
  const sepia = droopFactor * 0.42;
  const brightness = 1 - droopFactor * 0.18;

  // Friendly time string in Spanish
  let timeSinceText: string;
  if (!lastWateredAt) {
    timeSinceText = 'Sin registro de riego reciente';
  } else if (hoursElapsed < 1) {
    const mins = Math.max(1, Math.round(hoursElapsed * 60));
    timeSinceText = mins === 1 ? 'Regadas hace un momento' : `Regadas hace ${mins} minutos`;
  } else if (hoursElapsed < 24) {
    const h = Math.floor(hoursElapsed);
    timeSinceText = h === 1 ? 'Regadas hace 1 hora' : `Regadas hace ${h} horas`;
  } else {
    const d = Math.floor(daysElapsed);
    const remainingH = Math.floor(hoursElapsed % 24);
    if (d === 1) {
      timeSinceText = remainingH > 0 ? `Regadas hace 1 día y ${remainingH}h` : 'Regadas hace 1 día';
    } else {
      timeSinceText = `Hace ${d} días sin regar`;
    }
  }

  return {
    hydrationPercent,
    witherLevel,
    hoursElapsed,
    daysElapsed,
    stage,
    stageLabel,
    stageBadgeColor,
    timeSinceText,
    needsWater: hydrationPercent < 70,
    stemDroopDeg,
    headDroopDeg,
    leafDroopDeg,
    saturation,
    sepia,
    brightness,
  };
}

/**
 * Synthesizes a realistic, gentle water sprinkling sound effect using Web Audio API
 * No external audio files needed; guarantees 100% reliable sound across devices
 */
export function playWateringSound() {
  if (typeof window === 'undefined') return;
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const now = ctx.currentTime;

    // 1. Water trickle / spray noise layer
    const bufferSize = ctx.sampleRate * 1.8;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.9));
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(1200, now);
    filter.frequency.exponentialRampToValueAtTime(800, now + 1.5);
    filter.Q.setValueAtTime(3.5, now);

    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.01, now);
    noiseGain.gain.linearRampToValueAtTime(0.09, now + 0.2);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 1.8);

    noise.connect(filter);
    filter.connect(noiseGain);
    noiseGain.connect(ctx.destination);
    noise.start(now);

    // 2. Sparkling crystalline droplets (gentle melodic water plops)
    const pitches = [587.33, 739.99, 880.0, 1046.5, 1174.66, 880.0, 659.25]; // D5, F#5, A5, C6, D6, A5, E5
    pitches.forEach((freq, idx) => {
      const dropTime = now + 0.15 + idx * 0.22 + Math.random() * 0.08;
      const osc = ctx.createOscillator();
      const dropGain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, dropTime);
      osc.frequency.exponentialRampToValueAtTime(freq * 1.15, dropTime + 0.04);
      osc.frequency.exponentialRampToValueAtTime(freq * 0.95, dropTime + 0.18);

      dropGain.gain.setValueAtTime(0.001, dropTime);
      dropGain.gain.linearRampToValueAtTime(0.06, dropTime + 0.02);
      dropGain.gain.exponentialRampToValueAtTime(0.0001, dropTime + 0.25);

      osc.connect(dropGain);
      dropGain.connect(ctx.destination);

      osc.start(dropTime);
      osc.stop(dropTime + 0.26);
    });

    // Automatically close audio context after playing
    setTimeout(() => {
      try {
        ctx.close();
      } catch {}
    }, 2500);
  } catch (err) {
    // Non-blocking if audio policy restricts autoplay
    console.debug('Audio playback note:', err);
  }
}

/**
 * Returns today's calendar date formatted as YYYY-MM-DD in local time
 */
export function getTodayDateStr(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const DEFAULT_CAN_CAPACITY = 100;
export const WATER_PER_POUR = 5; // 5% consumed per pour (100% / 5% = 20 pours, smooth fixed decrement)
export const REFILL_CYCLE_HOURS = 24;
export const REFILL_CYCLE_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds (86,400,000 ms)

export interface CanWaterStatus {
  canWaterLevel: number; // 0 to 100
  isEmpty: boolean;
  isFull: boolean;
  needsDailyReset: boolean;
  todayStr: string;
  remainingPours: number;
  // 24-Hour countdown metrics
  isCountingDown: boolean;
  usedAt: string | null;
  refillAt: string | null;
  msElapsed: number;
  remainingMs: number;
  hoursLeft: number;
  minutesLeft: number;
  secondsLeft: number;
  formattedCountdown: string; // e.g. "23:59:58"
  countdownTextSpanish: string; // e.g. "23h 59m 58s"
  progressPercent: number; // 0 to 100% of the 24-hour refill progression
}

/**
 * Computes the current water level in the watering can and evaluates the 24-hour refill cycle.
 * The 24-hour timer starts automatically when the watering can is used (lastCanUsedAt / lastWateredAt).
 * When 24 hours elapse, the can automatically refills back to 100%.
 */
export function getEffectiveCanWaterLevel(
  watering?: {
    canWaterLevel?: number;
    lastWaterRefillDate?: string;
    lastCanUsedAt?: string | null;
    lastWateredAt?: string | null;
  } | null,
  currentTimeMs: number = Date.now()
): CanWaterStatus {
  const todayStr = getTodayDateStr();
  const currentSavedLevel =
    typeof watering?.canWaterLevel === 'number'
      ? Math.max(0, Math.min(100, Math.round(watering.canWaterLevel)))
      : 100;

  // 1. If can is full (100%), it is ready to use and NOT in an active countdown
  if (currentSavedLevel >= 100) {
    return {
      canWaterLevel: 100,
      isEmpty: false,
      isFull: true,
      needsDailyReset: false,
      todayStr,
      remainingPours: Math.floor(100 / WATER_PER_POUR), // 4 pours
      isCountingDown: false,
      usedAt: null,
      refillAt: null,
      msElapsed: 0,
      remainingMs: 0,
      hoursLeft: 0,
      minutesLeft: 0,
      secondsLeft: 0,
      formattedCountdown: '24:00:00',
      countdownTextSpanish: 'Lista para regar (100%)',
      progressPercent: 100,
    };
  }

  // 2. Can has less than 100% water: check if 24 hours have elapsed since it was used
  const usedTimestamp = watering?.lastCanUsedAt || watering?.lastWateredAt || null;

  if (usedTimestamp) {
    const startTime = Date.parse(usedTimestamp);
    if (!isNaN(startTime)) {
      const msElapsed = Math.max(0, currentTimeMs - startTime);

      // 24 hours have completely passed! Automatic refill to 100%
      if (msElapsed >= REFILL_CYCLE_MS) {
        return {
          canWaterLevel: 100,
          isEmpty: false,
          isFull: true,
          needsDailyReset: true,
          todayStr,
          remainingPours: Math.floor(100 / WATER_PER_POUR),
          isCountingDown: false,
          usedAt: usedTimestamp,
          refillAt: new Date(startTime + REFILL_CYCLE_MS).toISOString(),
          msElapsed,
          remainingMs: 0,
          hoursLeft: 0,
          minutesLeft: 0,
          secondsLeft: 0,
          formattedCountdown: '00:00:00',
          countdownTextSpanish: '¡Recarga de 24h completada (100%)!',
          progressPercent: 100,
        };
      }

      // Active 24-hour countdown in progress: water level is strictly non-infinite!
      const remainingMs = REFILL_CYCLE_MS - msElapsed;
      const totalSecondsLeft = Math.floor(remainingMs / 1000);
      const hoursLeft = Math.floor(totalSecondsLeft / 3600);
      const minutesLeft = Math.floor((totalSecondsLeft % 3600) / 60);
      const secondsLeft = totalSecondsLeft % 60;

      const formattedCountdown = `${String(hoursLeft).padStart(2, '0')}:${String(
        minutesLeft
      ).padStart(2, '0')}:${String(secondsLeft).padStart(2, '0')}`;

      const countdownTextSpanish = `${hoursLeft}h ${minutesLeft}m ${secondsLeft}s`;
      const progressPercent = Math.min(100, Math.max(0, (msElapsed / REFILL_CYCLE_MS) * 100));

      return {
        canWaterLevel: currentSavedLevel,
        isEmpty: currentSavedLevel <= 0,
        isFull: false,
        needsDailyReset: false,
        todayStr,
        remainingPours: Math.ceil(currentSavedLevel / WATER_PER_POUR),
        isCountingDown: true,
        usedAt: usedTimestamp,
        refillAt: new Date(startTime + REFILL_CYCLE_MS).toISOString(),
        msElapsed,
        remainingMs,
        hoursLeft,
        minutesLeft,
        secondsLeft,
        formattedCountdown,
        countdownTextSpanish,
        progressPercent,
      };
    }
  }

  // 3. Level is under 100% but timestamp wasn't recorded yet: start active countdown from now
  const fallbackStart = currentTimeMs;
  const remainingMs = REFILL_CYCLE_MS;
  const totalSecondsLeft = Math.floor(remainingMs / 1000);
  const hoursLeft = Math.floor(totalSecondsLeft / 3600);
  const minutesLeft = Math.floor((totalSecondsLeft % 3600) / 60);
  const secondsLeft = totalSecondsLeft % 60;
  const formattedCountdown = `${String(hoursLeft).padStart(2, '0')}:${String(
    minutesLeft
  ).padStart(2, '0')}:${String(secondsLeft).padStart(2, '0')}`;
  const countdownTextSpanish = `${hoursLeft}h ${minutesLeft}m ${secondsLeft}s`;

  return {
    canWaterLevel: currentSavedLevel,
    isEmpty: currentSavedLevel <= 0,
    isFull: false,
    needsDailyReset: false,
    todayStr,
    remainingPours: Math.ceil(currentSavedLevel / WATER_PER_POUR),
    isCountingDown: true,
    usedAt: new Date(fallbackStart).toISOString(),
    refillAt: new Date(fallbackStart + REFILL_CYCLE_MS).toISOString(),
    msElapsed: 0,
    remainingMs,
    hoursLeft,
    minutesLeft,
    secondsLeft,
    formattedCountdown,
    countdownTextSpanish,
    progressPercent: 0,
  };
}

